import {
  ExportacionesPorLlenarSchema,
  HistoricoDelPlaneadorSchema,
  OperacionDeBodegaSchema,
  ResultadoDeCargaDelPlaneadorSchema,
  ResultadoSimpleSchema,
  SemanaDelPlaneadorSchema,
  type EdicionDeOperacionDeBodega,
  type EdicionDeSemana,
  type NuevaOperacionDeBodega,
} from '@cnc/contracts';
import {keepPreviousData, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {json, pedir} from './cliente';

// Planeador semanal de la bodega (decision 0063).
export function useSemanaDelPlaneador(fecha: string) {
  return useQuery({
    queryKey: ['planeador', 'semana', fecha],
    queryFn: () => pedir(`planeador/semanas/${fecha}`, SemanaDelPlaneadorSchema),
    placeholderData: keepPreviousData,
  });
}

export function useHistoricoDelPlaneador(activo = true) {
  return useQuery({
    queryKey: ['planeador', 'historico'],
    queryFn: () => pedir('planeador/historico', HistoricoDelPlaneadorSchema),
    enabled: activo,
  });
}

export function useExportacionesPorLlenar() {
  return useQuery({
    queryKey: ['planeador', 'exportaciones-por-llenar'],
    queryFn: () => pedir('planeador/exportaciones-por-llenar', ExportacionesPorLlenarSchema),
    staleTime: 60_000,
  });
}

function useCambioDelPlaneador<A, R>(fn: (a: A) => Promise<R>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => cliente.invalidateQueries({queryKey: ['planeador']}),
  });
}

export const useCrearOperacion = () =>
  useCambioDelPlaneador((datos: NuevaOperacionDeBodega) =>
    pedir('planeador/operaciones', OperacionDeBodegaSchema, json('POST', datos)),
  );

export const useEditarOperacion = () =>
  useCambioDelPlaneador(({id, cambios}: {id: number; cambios: EdicionDeOperacionDeBodega}) =>
    pedir(`planeador/operaciones/${id}`, OperacionDeBodegaSchema, json('PATCH', cambios)),
  );

export const useEliminarOperacion = () =>
  useCambioDelPlaneador((id: number) => pedir(`planeador/operaciones/${id}`, ResultadoSimpleSchema, json('DELETE')));

export const useGuardarSemana = () =>
  useCambioDelPlaneador(({fecha, cambios}: {fecha: string; cambios: EdicionDeSemana}) =>
    pedir(`planeador/semanas/${fecha}`, SemanaDelPlaneadorSchema, json('PUT', cambios)),
  );

export const useCargarPlaneador = () =>
  useCambioDelPlaneador(({archivos, modo}: {archivos: File[]; modo: 'revisar' | 'aplicar'}) => {
    const cuerpo = new FormData();
    for (const a of archivos) cuerpo.append('archivos', a, a.name);
    cuerpo.append('modo', modo);
    // Si dos archivos traen la misma semana, manda el mas reciente.
    cuerpo.append('modificados', JSON.stringify(Object.fromEntries(archivos.map(a => [a.name, a.lastModified]))));
    return pedir('planeador/cargar', ResultadoDeCargaDelPlaneadorSchema, {method: 'POST', body: cuerpo});
  });

export const rutaExcelDeSemana = (fecha: string) => `/api/planeador/semanas/${fecha}/excel`;
